import { useI18n } from 'vue-i18n';
import { boolean, date, number, string } from 'yup';
import { useFormat } from './format';

export function useFormValidation() {
  const { t } = useI18n();
  const { formatDateToLocaleStringIgnoringTime } = useFormat();

  function dateRequiredValidation(field: string) {
    return date().required(
      t('formsValidations.rules_error_messages.required', {
        field: t(`formsValidations.fields.${field.split(':')[0]}`),
      }),
    );
  }

  function dateValidation(field: string, options: { required: boolean; minDate?: Date; maxDate?: Date }) {
    const label = t(`formsValidations.fields.${field.split(':')[0]}`);

    let schema = date();

    if (options.minDate) {
      schema = schema.required(t('formsValidations.rules_error_messages.required', { field: label }));
    }

    if (options.minDate) {
      schema = schema.min(
        options.minDate,
        t('formsValidations.rules_error_messages.min_date', {
          field: label,
          min_date: formatDateToLocaleStringIgnoringTime(options.minDate),
        }),
      );
    }

    if (options.maxDate) {
      schema = schema.max(
        options.maxDate,
        t('formsValidations.rules_error_messages.max_date', {
          field: label,
          max_date: formatDateToLocaleStringIgnoringTime(options.maxDate),
        }),
      );
    }

    return schema;
  }

  function numberValidationRequiredMinMaxValue(field: string, minValue: number, maxValue: number) {
    return number()
      .transform((value) => (Number.isNaN(value) ? 0 : value))
      .required('')
      .min(
        minValue,
        t('formsValidations.rules_error_messages.min_value', {
          field: t(`formsValidations.fields.${field}`),
          min_value: minValue,
        }),
      )
      .max(
        maxValue,
        t('formsValidations.rules_error_messages.max_value', {
          field: t(`formsValidations.fields.${field}`),
          max_value: maxValue,
        }),
      );
  }
  function numberValidationMaxValue(field: string, maxValue: number) {
    return number()
      .transform((value) => (Number.isNaN(value) ? 0 : value))
      .max(
        maxValue,
        t('formsValidations.rules_error_messages.max_value', {
          field: t(`formsValidations.fields.${field}`),
          max_value: maxValue,
        }),
      );
  }
  function textMaxCharsValidation(field: string, maxChars: number, ErrorText: string = '') {
    if (ErrorText) {
      return string().max(maxChars, ErrorText);
    }

    return string().max(
      maxChars,
      t('formsValidations.rules_error_messages.max_chars', {
        field: t(`formsValidations.fields.${field}`),
        max_chars: maxChars,
      }),
    );
  }
  function textRequiredValidation(field: string) {
    return string().required(
      t('formsValidations.rules_error_messages.required', {
        field: t(`formsValidations.fields.${field.split(':')[0]}`),
      }),
    );
  }
  function textRequiredMaxCharsValidation(field: string, maxChars: number) {
    return string()
      .required(
        t('formsValidations.rules_error_messages.required', {
          field: t(`formsValidations.fields.${field}`),
        }),
      )
      .max(
        maxChars,
        t('formsValidations.rules_error_messages.max_chars', {
          field: t(`formsValidations.fields.${field}`),
          max_chars: maxChars,
        }),
      );
  }
  function textRequiredMinMaxCharsValidation(field: string, minChars: number, maxChars: number) {
    return textRequiredMaxCharsValidation(field, maxChars).min(
      minChars,
      t('formsValidations.rules_error_messages.min_chars', {
        field: t(`formsValidations.fields.${field}`),
        min_chars: minChars,
      }),
    );
  }
  function phoneNumberValidation() {
    return textRequiredMinMaxCharsValidation('phone_number', 11, 8);
  }
  function emailValidation(field: string) {
    return textRequiredValidation(field).email('Must be a valid email!');
  }
  function checkboxValidation(field: string) {
    return boolean()
      .required()
      .oneOf([true], t(`formsValidations.fields.${field}`));
  }

  return {
    dateRequiredValidation,
    numberValidationRequiredMinMaxValue,
    numberValidationMaxValue,
    textMaxCharsValidation,
    textRequiredValidation,
    textRequiredMaxCharsValidation,
    textRequiredMinMaxCharsValidation,
    phoneNumberValidation,
    emailValidation,
    checkboxValidation,
    dateValidation,
  };
}
